import { motion } from "framer-motion";
import { ChatsCircle, Users, Heart, Lightning } from "@phosphor-icons/react";
import { photoById } from "../config/photos";

const pillars = [
    {
        icon: ChatsCircle,
        title: "Chat first",
        body: "Every stream is shaped by chat. Polls, callouts, side quests, weird rabbit holes — if you're typing, you're part of the show.",
        ring: "ring-cyan-400/40",
        accent: "bg-cyan-400",
    },
    {
        icon: Users,
        title: "Run by the mods",
        body: "realcpat, tanoshark, and rexynz04 keep the place sharp and welcoming. They've been here since day one and they set the tone.",
        ring: "ring-violet-400/40",
        accent: "bg-violet-400",
    },
    {
        icon: Heart,
        title: "Low-key, high-loyalty",
        body: "Small community, real conversations. People know each other's emotes, inside jokes, and clip history. Pull up once and you'll get it.",
        ring: "ring-rose-400/40",
        accent: "bg-rose-400",
    },
    {
        icon: Lightning,
        title: "Always something happening",
        body: "Subathons, sub-only nights, raid trains, and the occasional total chat meltdown. Boring days are not on the schedule.",
        ring: "ring-amber-400/40",
        accent: "bg-amber-400",
    },
];

export const CommunityIntro = () => {
    return (
        <section className="relative py-8 lg:py-12" data-testid="community-intro">
            <div className="max-w-6xl mx-auto px-6 lg:px-8">
                {/* Cinematic photo banner */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="relative overflow-hidden rounded-2xl border border-white/10 mb-6"
                    style={{ height: 280 }}
                    data-testid="community-banner-photo"
                >
                    <img
                        src={photoById("aura").src}
                        alt={photoById("aura").alt}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div
                        aria-hidden
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background:
                                "linear-gradient(90deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.6) 100%), radial-gradient(600px 280px at 80% 60%, rgba(34,211,238,0.18), transparent 65%)",
                        }}
                    />
                    <div className="relative h-full flex flex-col justify-end p-6 lg:p-8">
                        <span className="eyebrow text-cyan-300/80 mb-1">From the chair</span>
                        <h2 className="font-display text-2xl sm:text-3xl text-white leading-tight max-w-xl">
                            Real streams. Real chat. Built one community at a time.
                        </h2>
                    </div>
                </motion.div>

                {/* Description card */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="relative overflow-hidden rounded-2xl border border-white/10 bg-obsidian-900/70 p-7 lg:p-9 mb-8"
                    data-testid="community-description"
                >
                    <div
                        aria-hidden
                        className="absolute inset-0 pointer-events-none opacity-90"
                        style={{
                            background:
                                "radial-gradient(800px 280px at 10% 10%, rgba(34,211,238,0.16), transparent 60%), radial-gradient(700px 280px at 92% 90%, rgba(196,160,255,0.16), transparent 65%)",
                        }}
                    />
                    <div className="relative grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6 lg:gap-10 items-start">
                        <div className="flex items-center gap-3">
                            <div className="h-12 w-12 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0">
                                <Users size={22} className="text-cyan-300" weight="fill" />
                            </div>
                            <div className="lg:hidden">
                                <div className="eyebrow text-cyan-300/80">What this is</div>
                                <div className="font-display text-white text-xl">A real community.</div>
                            </div>
                        </div>
                        <div>
                            <div className="hidden lg:block eyebrow text-cyan-300/80 mb-2">What this is</div>
                            <h2 className="hidden lg:block font-display text-3xl text-white leading-tight mb-3">
                                Not a fan-club. <span className="gradient-text">A real community.</span>
                            </h2>
                            <p className="text-white/70 leading-relaxed">
                                ools.me isn't built around one person yelling at a camera. The whole reason this exists is the
                                people who keep showing up — the mods running things, the regulars dropping clips in Discord, the
                                lurkers who finally type something at 2 AM. Chat literally changes what game I open. Everyone
                                here has a name, an emote, or a story we keep coming back to.
                            </p>
                            <p className="text-white/55 leading-relaxed mt-3 text-[15px]">
                                If you're new: hop in the Discord, say hi, drop into a stream, and stick around. That's the
                                whole onboarding. We'll do the rest.
                            </p>
                        </div>
                    </div>
                </motion.div>

                {/* Pillars */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="community-pillars">
                    {pillars.map((p, i) => {
                        const Icon = p.icon;
                        return (
                            <motion.div
                                key={p.title}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: i * 0.06 }}
                                className={`relative rounded-2xl border border-white/10 bg-obsidian-900/60 p-5 ring-1 ${p.ring}`}
                                data-testid={`community-pillar-${i}`}
                            >
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                                        <Icon size={15} weight="bold" className="text-white/85" />
                                    </div>
                                    <span className={`h-1.5 w-1.5 rounded-full ${p.accent}`} />
                                </div>
                                <h3 className="font-display text-white text-base">{p.title}</h3>
                                <p className="text-white/55 text-sm mt-1.5 leading-relaxed">{p.body}</p>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
